import React, { useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox } from 'rsuite'
import { ValueType } from 'rsuite/esm/Checkbox'
import { useTranslation } from 'react-i18next'
import {
  PUBLIC_ADMINISTRATION_FILTER_STATE_KEYS,
  type PublicAdministrationFilterCounts
} from 'domain/feature/hub/company/Ecosystem/publicAdministrationFilters'
import { useEcosystemFilterPanelDismiss } from '../useEcosystemFilterPanel'
import {
  MARCO_LEGAL_OPTIONS,
  OBJETIVOS_OPTIONS,
  publicAdministrationBooleanFilterOptions as resolvePublicAdministrationBooleanFilterOptions,
  publicAdministrationBrazilStateOptions,
  publicAdministrationOrganTypeOptions,
  publicAdministrationPopulationSizeOptions as resolvePublicAdministrationPopulationSizeOptions,
  VERTICAIS_OPTIONS
} from './constants'
import {
  PublicAdministrationFilterCheckboxList,
  PublicAdministrationFilterCheckboxRow,
  PublicAdministrationFilterDivider,
  PublicAdministrationFilterGroupClear,
  PublicAdministrationFilterGroupHeader,
  PublicAdministrationFilterGroupTitle,
  PublicAdministrationFilterPanelClearAll,
  PublicAdministrationFilterPanelHeader,
  PublicAdministrationFilterPanelRoot,
  PublicAdministrationFilterPanelTitle
} from './styles'

interface PublicAdministrationMultiFilterProps {
  isExpanded: boolean
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>
  onFilterChange: (type: string, values: ValueType[], checked: boolean) => void
  filterCounts: PublicAdministrationFilterCounts
  selectedFilters?: Record<string, string[]>
  filterBarRef?: React.RefObject<HTMLDivElement | null>
}

const FilterGroupHeading = ({ children, onClear }: { children: React.ReactNode; onClear: () => void }): JSX.Element => (
  <PublicAdministrationFilterGroupHeader>
    <PublicAdministrationFilterGroupTitle>{children}</PublicAdministrationFilterGroupTitle>
    <PublicAdministrationFilterGroupClear onClick={onClear} title="Limpar filtro">
      <FontAwesomeIcon icon={['fas', 'times']} style={{ fontSize: 14 }} />
    </PublicAdministrationFilterGroupClear>
  </PublicAdministrationFilterGroupHeader>
)

export const PublicAdministrationMultiFilter = ({
  setIsExpanded,
  onFilterChange,
  filterCounts,
  selectedFilters = {},
  filterBarRef
}: PublicAdministrationMultiFilterProps): JSX.Element => {
  const { t, i18n } = useTranslation()
  const panelContentRef = useRef<HTMLDivElement>(null)
  const alignPanelToFilterBarRight = useEcosystemFilterPanelDismiss(
    setIsExpanded,
    filterBarRef,
    panelContentRef
  )

  const publicAdministrationPopulationSizeOptions = useMemo(
    () => resolvePublicAdministrationPopulationSizeOptions(t),
    [t, i18n.language]
  )
  const publicAdministrationBooleanFilterOptions = useMemo(
    () => resolvePublicAdministrationBooleanFilterOptions(t),
    [t, i18n.language]
  )

  const selectedAdministrationTypes = selectedFilters.pa_administration_type ?? []
  const selectedStates = selectedFilters.pa_state ?? []
  const selectedPopulationSizes = selectedFilters.pa_population_size ?? []
  const selectedHasInnovationDepartment = selectedFilters.pa_has_innovation_department ?? []
  const selectedHasCtBudget = selectedFilters.pa_has_ct_budget ?? []
  const selectedImpactAreas = selectedFilters.pa_impact_area ?? []
  const selectedProcedures = selectedFilters.pa_procedure ?? []
  const selectedProgramGoals = selectedFilters.pa_program_goal ?? []

  const handleClearAllFilters = (): void => {
    PUBLIC_ADMINISTRATION_FILTER_STATE_KEYS.forEach(filterKey => {
      onFilterChange(filterKey, [], false)
    })
  }

  const updateMultiSelectFilter = (
    filterKey: string,
    currentValues: string[],
    rawValue: ValueType | undefined,
    isChecked: boolean
  ): void => {
    if (rawValue === undefined) return
    const valueString = String(rawValue)
    const nextValues = isChecked
      ? [...currentValues, valueString]
      : currentValues.filter(v => v !== valueString)
    onFilterChange(filterKey, nextValues, nextValues.length > 0)
  }

  const panelContent = (
    <PublicAdministrationFilterPanelRoot
      ref={panelContentRef}
      $alignToFilterBarRight={alignPanelToFilterBarRight}
    >
      <PublicAdministrationFilterPanelHeader>
        <PublicAdministrationFilterPanelTitle>Filtros</PublicAdministrationFilterPanelTitle>
        <PublicAdministrationFilterPanelClearAll onClick={handleClearAllFilters} role="button">
          Limpar
        </PublicAdministrationFilterPanelClearAll>
      </PublicAdministrationFilterPanelHeader>

      <FilterGroupHeading onClear={() => onFilterChange('pa_administration_type', [], false)}>
        Tipo de Órgão{filterCounts.pa_administration_type > 0 ? ` (${filterCounts.pa_administration_type})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {publicAdministrationOrganTypeOptions.map((option, index) => (
          <PublicAdministrationFilterCheckboxRow key={`organ-type-${String(option.label)}`}>
            <Checkbox
              checked={selectedAdministrationTypes.includes(String(index))}
              value={index as unknown as ValueType}
              onChange={(value, checked) =>
                updateMultiSelectFilter('pa_administration_type', selectedAdministrationTypes, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_state', [], false)}>
        Estado{filterCounts.pa_state > 0 ? ` (${filterCounts.pa_state})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {publicAdministrationBrazilStateOptions.map(option => (
          <PublicAdministrationFilterCheckboxRow key={option.value}>
            <Checkbox
              checked={selectedStates.includes(option.value)}
              value={option.value}
              onChange={(value, checked) => updateMultiSelectFilter('pa_state', selectedStates, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_population_size', [], false)}>
        Porte Populacional{filterCounts.pa_population_size > 0 ? ` (${filterCounts.pa_population_size})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {publicAdministrationPopulationSizeOptions.map(option => (
          <PublicAdministrationFilterCheckboxRow key={option.id}>
            <Checkbox
              checked={selectedPopulationSizes.includes(String(option.id))}
              value={option.id as unknown as ValueType}
              onChange={(value, checked) =>
                updateMultiSelectFilter('pa_population_size', selectedPopulationSizes, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_has_innovation_department', [], false)}>
        Possui Área de Inovação
        {filterCounts.pa_has_innovation_department > 0 ? ` (${filterCounts.pa_has_innovation_department})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {publicAdministrationBooleanFilterOptions.map(option => (
          <PublicAdministrationFilterCheckboxRow key={`innovation-dept-${option.value}`}>
            <Checkbox
              checked={selectedHasInnovationDepartment.includes(option.value)}
              value={option.value}
              onChange={(value, checked) =>
                updateMultiSelectFilter(
                  'pa_has_innovation_department',
                  selectedHasInnovationDepartment,
                  value,
                  checked
                )}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_has_ct_budget', [], false)}>
        Possui Orçamento CT&I{filterCounts.pa_has_ct_budget > 0 ? ` (${filterCounts.pa_has_ct_budget})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {publicAdministrationBooleanFilterOptions.map(option => (
          <PublicAdministrationFilterCheckboxRow key={`ct-budget-${option.value}`}>
            <Checkbox
              checked={selectedHasCtBudget.includes(option.value)}
              value={option.value}
              onChange={(value, checked) =>
                updateMultiSelectFilter('pa_has_ct_budget', selectedHasCtBudget, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_impact_area', [], false)}>
        Vertical de Impacto{filterCounts.pa_impact_area > 0 ? ` (${filterCounts.pa_impact_area})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {VERTICAIS_OPTIONS.map((option, index) => (
          <PublicAdministrationFilterCheckboxRow key={option.id}>
            <Checkbox
              checked={selectedImpactAreas.includes(String(index))}
              value={index as unknown as ValueType}
              onChange={(value, checked) =>
                updateMultiSelectFilter('pa_impact_area', selectedImpactAreas, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_procedure', [], false)}>
        Procedimentos Marco Legal{filterCounts.pa_procedure > 0 ? ` (${filterCounts.pa_procedure})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {MARCO_LEGAL_OPTIONS.map((option, index) => (
          <PublicAdministrationFilterCheckboxRow key={option.id}>
            <Checkbox
              checked={selectedProcedures.includes(String(index))}
              value={index as unknown as ValueType}
              onChange={(value, checked) =>
                updateMultiSelectFilter('pa_procedure', selectedProcedures, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>

      <PublicAdministrationFilterDivider />

      <FilterGroupHeading onClear={() => onFilterChange('pa_program_goal', [], false)}>
        Objetivo no Programa{filterCounts.pa_program_goal > 0 ? ` (${filterCounts.pa_program_goal})` : ''}
      </FilterGroupHeading>
      <PublicAdministrationFilterCheckboxList>
        {OBJETIVOS_OPTIONS.map((option, index) => (
          <PublicAdministrationFilterCheckboxRow key={option.id}>
            <Checkbox
              checked={selectedProgramGoals.includes(String(index))}
              value={index as unknown as ValueType}
              onChange={(value, checked) =>
                updateMultiSelectFilter('pa_program_goal', selectedProgramGoals, value, checked)}
            >{` ${option.label}`}</Checkbox>
          </PublicAdministrationFilterCheckboxRow>
        ))}
      </PublicAdministrationFilterCheckboxList>
    </PublicAdministrationFilterPanelRoot>
  )

  if (alignPanelToFilterBarRight && filterBarRef?.current != null) {
    return createPortal(panelContent, filterBarRef.current)
  }
  return panelContent
}
